import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { getSceneDetail, getSolutionDetail, type SubScene } from "@/api/solutionApi";
import { getProductById, type Product } from "@/api/productApi";
import { getCaseById } from "@/api/caseApi";
import DetailHero from "./components/DetailHero";
import DetailIntro from "./components/DetailIntro";
import DetailPainPoints from "./components/DetailPainPoints";
import DetailBenefits from "./components/DetailBenefits";
import DetailCases from "./components/DetailCases";
import DetailProducts from "./components/DetailProducts";
import { useSEO, buildBreadcrumbLd } from "@/utils/seo";

export default function SolutionSceneDetail() {
  const { subId } = useParams<{ subId: string }>();
  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);

  const [scene, setScene] = useState<SubScene | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [relatedCases, setRelatedCases] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<string>("");

  useSEO(
    scene
      ? {
          title: `${scene.name} | ${scene.parentName || "解决方案"}`,
          description:
            (scene.introduction || scene.desc || "").slice(0, 160) ||
            `欧格登${scene.parentName || ""}${scene.name}解决方案详情，了解方案价值、实施路径与成功案例。`,
          keywords: [
            scene.name,
            scene.parentName || "解决方案",
            ...(scene.introTags || scene.tags || []),
          ].filter(Boolean) as string[],
          path: `/solutions/detail/${subId}`,
          image: scene.heroImage || scene.image || undefined,
          ogType: "article",
          jsonLd: buildBreadcrumbLd([
            { name: "首页", path: "/" },
            { name: "解决方案", path: "/solutions" },
            ...(scene.parentCategory && scene.parentName
              ? [{ name: scene.parentName, path: `/solutions?category=${scene.parentCategory}` }]
              : []),
            { name: scene.name, path: `/solutions/detail/${subId}` },
          ]),
        }
      : {
          title: "解决方案详情",
          description: "欧格登解决方案详情页，加载中...",
          path: `/solutions/detail/${subId ?? ""}`,
        },
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [subId]);

  // 滚动监听：追踪当前可见的版块
  useEffect(() => {
    // 顺序需与页面 DOM 顺序一致（相关产品已上移至 Tab 栏下方）
    const sectionIds = ["section-products", "section-pain-points", "section-intro", "section-benefits", "section-cases"];
    const handleScroll = () => {
      const offset = 160;
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.getBoundingClientRect().top <= offset) {
          setActiveSection(sectionIds[i].replace("section-", ""));
          return;
        }
      }
      setActiveSection("");
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scene]);

  useEffect(() => {
    if (!subId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setRelatedProducts([]);
    setRelatedCases([]);
    // 先尝试按方案ID查询（从列表页传来的数字ID），失败则按场景标识查询
    getSolutionDetail(subId)
      .then((res: any) => {
        const data = res.data || res;
        if (data && data.title) {
          // 将后台方案数据适配为 SubScene 格式
          setScene({
            id: String(data.id),
            icon: data.icon || "",
            name: data.title,
            desc: data.description || "",
            tags: data.tags || [],
            productCategory: data.category || "",
            image: data.image || "",
            parentCategory: data.category || "",
            parentName: data.categoryName || "",
            accentColor: data.category === "info-security" ? "#1a56db" : data.category === "it-infrastructure" ? "#0891b2" : "#059669",
            heroImage: data.image || "",
            // 方案简介：优先使用 intro 字段（方案简介详情），回退到 description
            introduction: data.intro || data.description || "",
            // 方案简介标签：优先使用 introTags，回退到 tags
            introTags: data.introTags || data.tags || [],
            // 方案简介配图
            introImage: data.introImage || "",
            // 痛点分析配图
            painPointsImage: data.painPointsImage || "",
            // 痛点分析：从 painPoints 子表读取
            painPoints: (data.painPoints || []).map((p: any) => ({
              icon: p.icon || "",
              title: p.title || "",
              desc: p.desc || p.description || "",
            })),
            // 解决方案：从 features 子表读取（后端返回的是 features 数组）
            features: (data.features || []).map((f: any) => ({
              icon: f.icon || "",
              title: f.name || f.title || "",
              desc: f.desc || f.description || "",
            })),
            // 方案价值：从 values 子表读取（后端返回的是 values 数组）
            benefits: (data.values || []).map((v: any) => ({
              icon: v.icon || "",
              title: v.direction || "",
              value: v.metric || "",
              desc: v.desc || v.description || "",
            })),
            relatedCaseSolution: data.category || "",
          });

          // 加载关联产品
          const productIds: number[] = data.relatedProductIds || [];
          if (productIds.length > 0) {
            Promise.allSettled(productIds.map((pid: number) => getProductById(pid)))
              .then((results) => {
                const products = results
                  .filter((r): r is PromiseFulfilledResult<Product> => r.status === "fulfilled" && !!r.value)
                  .map((r) => r.value);
                setRelatedProducts(products);
              });
          }

          // 加载关联案例
          const caseIds: number[] = data.relatedCaseIds || [];
          if (caseIds.length > 0) {
            Promise.allSettled(caseIds.map((cid: number) => getCaseById(cid)))
              .then((results) => {
                const cases = results
                  .filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled" && !!r.value)
                  .map((r) => {
                    const c = r.value.data || r.value;
                    return c;
                  });
                setRelatedCases(cases);
              });
          }
        } else {
          setScene(null);
        }
      })
      .catch(() => {
        // 回退到按场景标识查询
        return getSceneDetail(subId)
          .then((data) => setScene(data))
          .catch(() => {
            setError("加载方案详情失败，请稍后重试");
            setScene(null);
          });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [subId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        {/* Hero skeleton */}
        <div className="w-full h-[420px] md:h-[560px] bg-gray-100 animate-pulse" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-4">
              <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-full animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-5/6 animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <div className="text-center mt-32">
          <i className="ri-wifi-off-line text-5xl text-gray-300 mb-4 block"></i>
          <h2 className="text-xl font-bold text-gray-600 mb-2">加载失败</h2>
          <p className="text-gray-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-[#1a56db] text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  if (!scene) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <div className="text-center mt-32">
          <i className="ri-error-warning-line text-5xl text-gray-300 mb-4 block"></i>
          <h2 className="text-xl font-bold text-gray-600 mb-2">方案不存在</h2>
          <p className="text-gray-400 text-sm mb-6">未找到对应的解决方案详情</p>
          <button
            onClick={() => navigate("/solutions")}
            className="px-6 py-2.5 bg-[#1a56db] text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap"
          >
            返回解决方案
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" ref={topRef}>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Fixed Back Button - Top Left */}
      <button
        onClick={() => navigate(scene.parentCategory ? `/solutions?category=${scene.parentCategory}` : "/solutions")}
        className="fixed left-6 z-50 w-11 h-11 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer"
        style={{ top: "100px", backgroundColor: scene.accentColor, boxShadow: `0 4px 16px ${scene.accentColor}55` }}
        aria-label="返回"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>

      {/* 面包屑 */}
      <div className="pt-20 lg:pt-24 bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3">
          <nav className="flex items-center gap-2 text-xs text-gray-400">
            <Link to="/" className="hover:text-gray-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/solutions" className="hover:text-gray-600 transition-colors cursor-pointer">解决方案</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/solutions?category=${scene.parentCategory}`}
              className="hover:text-gray-600 transition-colors cursor-pointer"
            >
              {scene.parentName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span style={{ color: scene.accentColor }} className="font-medium">{scene.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <DetailHero scene={scene} />

      {/* 导航Tab栏 */}
      {(() => {
        const tabs = [
          ...(relatedProducts.length > 0 ? [{ id: "products", label: "相关产品" }] : []),
          ...(scene.painPoints && scene.painPoints.length > 0 ? [{ id: "pain-points", label: "痛点分析" }] : []),
          { id: "intro", label: "方案简介" },
          { id: "benefits", label: "方案价值" },
          ...(relatedCases.length > 0 ? [{ id: "cases", label: "相关案例" }] : []),
        ];
        return (
          <div className="sticky top-16 lg:top-[72px] z-40 bg-white border-b border-gray-100 shadow-sm">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
              <div className="flex items-center gap-0 overflow-x-auto hide-scrollbar">
                {tabs.map((tab) => {
                  const isActive = activeSection === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        const el = document.getElementById(`section-${tab.id}`);
                        if (el) {
                          const top = el.getBoundingClientRect().top + window.scrollY - 130;
                          window.scrollTo({ top, behavior: "smooth" });
                        }
                      }}
                      className="px-5 py-4 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer border-b-2"
                      style={{
                        color: isActive ? scene.accentColor : "#6b7280",
                        borderColor: isActive ? scene.accentColor : "transparent",
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 方案相关产品（紧随 Tab 栏展示） */}
      <div id="section-products">
        <DetailProducts products={relatedProducts} accentColor={scene.accentColor} />
      </div>

      {/* 痛点分析 */}
      {scene.painPoints && scene.painPoints.length > 0 && (
        <div id="section-pain-points">
          <DetailPainPoints
            painPoints={scene.painPoints}
            sceneName={scene.name}
            accentColor={scene.accentColor}
            painPointsImage={scene.painPointsImage}
          />
        </div>
      )}

      {/* 方案简介 */}
      <div id="section-intro">
        <DetailIntro scene={scene} />
      </div>

      {/* 价值总结 */}
      <div id="section-benefits">
        <DetailBenefits scene={scene} />
      </div>

      {/* 相关案例 */}
      <div id="section-cases">
        <DetailCases cases={relatedCases} accentColor={scene.accentColor} />
      </div>

      {/* CTA */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: `${scene.accentColor}08` }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 border"
            style={{ borderColor: `${scene.accentColor}30`, backgroundColor: `${scene.accentColor}10` }}
          >
            <i className="ri-customer-service-2-line text-xs" style={{ color: scene.accentColor }}></i>
            <span className="text-xs font-semibold" style={{ color: scene.accentColor }}>专业顾问团队</span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-4">
            想了解更多关于{scene.name}的方案？
          </h2>
          <p className="text-gray-500 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
            我们的专业顾问团队将根据您的业务需求，为您量身定制最适合的解决方案，提供免费咨询与方案评估服务。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/#contact-form"
              className="px-8 py-3 text-sm font-semibold rounded-full text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: scene.accentColor }}
            >
              免费获取方案
            </a>
            <Link
              to="/solutions"
              className="px-8 py-3 text-sm font-semibold rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ borderColor: `${scene.accentColor}40`, color: scene.accentColor }}
            >
              查看其他方案
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
